import React, { useContext } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { AuthContext, AuthProvider } from './contexts/auth/AuthContext';
import { FeatureProvider } from './contexts/auth/FeatureContext';
import LoginForm from './modules/profile-manager/LoginForm';
import ProfileRouteWrapper from './modules/profile-manager/ProfileRouteWrapper';
import { RoleBasedRoute, DashboardRedirect } from './modules/profile-manager/RoleBasedRoute';
import DashboardContainer from './modules/profile-manager/DashboardContainer';
import AdminDashboard from './modules/profile-manager/bulkopsadmin/AdminDashboard';
import AdminProfileEditor from './modules/profile-manager/bulkopsadmin/AdminProfileEditor';
import BillingDashboard from './modules/profile-manager/BillingDashboard';
import TrialUpgradePage from './modules/profile-manager/TrailUpgradePage';
import HelpCenter from './modules/profile-manager/HelpCenter';

// Signed-in pages share the drawer + header layout
const DashboardLayout = () => {
  const { currentUser, isAuthenticated, isLoading, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) return <Navigate to="/profiles/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate('/profiles/login', { replace: true });
  };

  return (
    <DashboardContainer
      userName={currentUser?.name || currentUser?.email}
      userRole={currentUser?.role}
      notifications={[]}
      onLogout={handleLogout}
      avatarUrl={currentUser?.avatarUrl}
    >
      <Outlet />
    </DashboardContainer>
  );
};

const adminOnly = (element) => <RoleBasedRoute requiredRole="admin">{element}</RoleBasedRoute>;

function App() {
  return (
    <BrowserRouter>
      <CssBaseline />
      <AuthProvider>
        <FeatureProvider>
          <Routes>
            <Route path="/profiles/login" element={<LoginForm />} />
            <Route element={<DashboardLayout />}>
              <Route path="/profiles/profile" element={<ProfileRouteWrapper />} />
              <Route path="/profiles/billing" element={<BillingDashboard />} />
              <Route path="/profiles/upgrade" element={<TrialUpgradePage />} />
              <Route path="/profiles/help" element={<HelpCenter />} />
              <Route path="/profiles/admin" element={adminOnly(<AdminDashboard />)} />
              <Route path="/profiles/admin/billing" element={adminOnly(<BillingDashboard />)} />
              <Route path="/profiles/admin/edit-user/:userId" element={adminOnly(<AdminProfileEditor />)} />
            </Route>
            <Route path="/upgrade" element={<Navigate to="/profiles/upgrade" replace />} />
            <Route path="/help" element={<Navigate to="/profiles/help" replace />} />
            <Route path="*" element={<DashboardRedirect />} />
          </Routes>
        </FeatureProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
